/**
 * Cross-Browser Animation Testing Utilities
 * Provides automated testing and validation for animations across different browsers
 */

import { browserCompatibility } from './browserCompatibility';

export interface AnimationTestResult {
  testName: string;
  browser: string;
  passed: boolean;
  duration: number;
  frameRate: number;
  errors: string[];
  warnings: string[];
  performance: {
    averageFPS: number;
    droppedFrames: number;
    memoryUsage: number;
  };
}

export interface AnimationTest {
  name: string;
  element: HTMLElement;
  animation: Animation | null;
  expectedDuration: number;
  expectedFrameRate: number;
  tolerance: number;
}

class CrossBrowserAnimationTester {
  private tests: Map<string, AnimationTest> = new Map();
  private results: AnimationTestResult[] = [];
  private performanceObserver: PerformanceObserver | null = null;

  constructor() {
    this.initializePerformanceMonitoring();
  }

  private initializePerformanceMonitoring(): void {
    if ('PerformanceObserver' in window) {
      this.performanceObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          if (entry.entryType === 'measure' && entry.name.startsWith('animation-test-')) {
            this.recordPerformanceMetric(entry);
          }
        });
      });
      
      this.performanceObserver.observe({ entryTypes: ['measure', 'navigation', 'paint'] });
    }
  }

  private recordPerformanceMetric(entry: PerformanceEntry): void {
    // Store performance metrics for analysis
    console.log(`Performance metric: ${entry.name} - ${entry.duration}ms`);
  }

  public registerTest(
    name: string,
    element: HTMLElement,
    animationConfig: {
      keyframes: Keyframe[];
      options: KeyframeAnimationOptions;
    }
  ): void {
    const animation = element.animate(animationConfig.keyframes, animationConfig.options);
    
    this.tests.set(name, {
      name,
      element,
      animation,
      expectedDuration: animationConfig.options.duration as number || 1000,
      expectedFrameRate: 60,
      tolerance: 0.1 // 10% tolerance
    });
  }

  public async runTest(testName: string): Promise<AnimationTestResult> {
    const test = this.tests.get(testName);
    if (!test) {
      throw new Error(`Test "${testName}" not found`);
    }

    const browserInfo = browserCompatibility.getBrowserInfo();
    const startTime = performance.now();
    
    performance.mark(`animation-test-${testName}-start`);

    const result: AnimationTestResult = {
      testName,
      browser: `${browserInfo.name} ${browserInfo.version}`,
      passed: false,
      duration: 0,
      frameRate: 0,
      errors: [],
      warnings: [],
      performance: {
        averageFPS: 0,
        droppedFrames: 0,
        memoryUsage: 0
      }
    };

    try {
      // Test animation execution
      if (test.animation) {
        await this.testAnimationExecution(test, result);
      }

      // Test performance
      await this.testAnimationPerformance(test, result);

      // Test browser-specific issues
      this.testBrowserSpecificIssues(test, result);

      // Test accessibility
      this.testAccessibility(test, result);

      result.passed = result.errors.length === 0;

    } catch (error) {
      result.errors.push(`Test execution failed: ${error}`);
      result.passed = false;
    }

    performance.mark(`animation-test-${testName}-end`);
    performance.measure(
      `animation-test-${testName}`,
      `animation-test-${testName}-start`,
      `animation-test-${testName}-end`
    );

    result.duration = performance.now() - startTime;
    this.results.push(result);

    return result;
  }

  private async testAnimationExecution(
    test: AnimationTest,
    result: AnimationTestResult
  ): Promise<void> {
    if (!test.animation) {
      result.errors.push('Animation failed to initialize');
      return;
    }

    // Test animation timing
    const actualDuration = test.animation.effect?.getTiming().duration as number;
    const expectedDuration = test.expectedDuration;
    const durationTolerance = expectedDuration * test.tolerance;

    if (Math.abs(actualDuration - expectedDuration) > durationTolerance) {
      result.warnings.push(
        `Animation duration mismatch: expected ${expectedDuration}ms, got ${actualDuration}ms`
      );
    }

    // Test animation state changes
    return new Promise((resolve) => {
      const checkStates = () => {
        const playState = test.animation?.playState;
        if (playState === 'finished') {
          resolve();
        } else if (playState === 'idle') {
          result.errors.push('Animation never started');
          resolve();
        }
      };

      test.animation.addEventListener('finish', () => {
        checkStates();
      });

      test.animation.addEventListener('cancel', () => {
        result.errors.push('Animation was cancelled unexpectedly');
        resolve();
      });

      // Timeout fallback
      setTimeout(() => {
        if (test.animation?.playState !== 'finished') {
          result.warnings.push('Animation did not complete within expected time');
        }
        resolve();
      }, expectedDuration + 1000);
    });
  }

  private async testAnimationPerformance(
    test: AnimationTest,
    result: AnimationTestResult
  ): Promise<void> {
    let frameCount = 0;
    let lastTime = performance.now();
    const frameTimes: number[] = [];

    const measureFrame = () => {
      const currentTime = performance.now();
      const deltaTime = currentTime - lastTime;
      frameTimes.push(deltaTime);
      frameCount++;
      lastTime = currentTime;

      if (test.animation?.playState === 'running') {
        requestAnimationFrame(measureFrame);
      } else {
        // Calculate performance metrics
        const averageFrameTime = frameTimes.reduce((a, b) => a + b, 0) / frameTimes.length;
        const averageFPS = 1000 / averageFrameTime;
        const droppedFrames = frameTimes.filter(time => time > 16.67).length; // 60fps = 16.67ms per frame

        result.performance.averageFPS = averageFPS;
        result.performance.droppedFrames = droppedFrames;

        // Performance thresholds
        if (averageFPS < 30) {
          result.errors.push(`Poor performance: ${averageFPS.toFixed(1)} FPS`);
        } else if (averageFPS < 50) {
          result.warnings.push(`Moderate performance: ${averageFPS.toFixed(1)} FPS`);
        }

        if (droppedFrames > frameCount * 0.1) {
          result.warnings.push(`High frame drop rate: ${droppedFrames}/${frameCount} frames`);
        }
      }
    };

    if (test.animation?.playState === 'running') {
      requestAnimationFrame(measureFrame);
    }
  }

  private testBrowserSpecificIssues(
    test: AnimationTest,
    result: AnimationTestResult
  ): void {
    const browserInfo = browserCompatibility.getBrowserInfo();
    const element = test.element;

    // Test Safari-specific issues
    if (browserInfo.name === 'safari') {
      this.testSafariIssues(element, result);
    }

    // Test Firefox-specific issues
    if (browserInfo.name === 'firefox') {
      this.testFirefoxIssues(element, result);
    }

    // Test Chrome-specific issues
    if (browserInfo.name === 'chrome') {
      this.testChromeIssues(element, result);
    }

    // Test Edge-specific issues
    if (browserInfo.name === 'edge') {
      this.testEdgeIssues(element, result);
    }
  }

  private testSafariIssues(element: HTMLElement, result: AnimationTestResult): void {
    // Test for Safari's transform-style: preserve-3d issues
    const computedStyle = getComputedStyle(element);
    
    if (computedStyle.transformStyle === 'preserve-3d') {
      const parent = element.parentElement;
      if (parent && getComputedStyle(parent).overflow !== 'visible') {
        result.warnings.push('Safari: preserve-3d may not work with non-visible overflow on parent');
      }
    }

    // Test for backdrop-filter support
    if (computedStyle.backdropFilter && computedStyle.backdropFilter !== 'none') {
      const browserInfo = browserCompatibility.getBrowserInfo();
      if (browserInfo.version < 14) {
        result.errors.push('Safari: backdrop-filter not supported in this version');
      }
    }
  }

  private testFirefoxIssues(element: HTMLElement, result: AnimationTestResult): void {
    // Test for Firefox transform performance issues
    const computedStyle = getComputedStyle(element);
    
    if (computedStyle.transform !== 'none' && !computedStyle.willChange.includes('transform')) {
      result.warnings.push('Firefox: Consider adding will-change: transform for better performance');
    }
  }

  private testChromeIssues(element: HTMLElement, result: AnimationTestResult): void {
    // Test for Chrome-specific optimizations
    const computedStyle = getComputedStyle(element);
    
    if (computedStyle.transform !== 'none' && computedStyle.transform.indexOf('translateZ') === -1) {
      result.warnings.push('Chrome: Consider using translateZ(0) for GPU acceleration');
    }
  }

  private testEdgeIssues(element: HTMLElement, result: AnimationTestResult): void {
    const browserInfo = browserCompatibility.getBrowserInfo();
    
    // Test for legacy Edge issues
    if (browserInfo.version < 79) {
      result.warnings.push('Edge: Legacy Edge has limited animation support');
    }
  }

  private testAccessibility(test: AnimationTest, result: AnimationTestResult): void {
    // Test for reduced motion compliance
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (prefersReducedMotion && test.animation?.playState === 'running') {
      result.warnings.push('Animation running despite prefers-reduced-motion setting');
    }

    // Test for keyboard accessibility
    const element = test.element;
    if (element.tabIndex >= 0 && !element.getAttribute('aria-label')) {
      result.warnings.push('Interactive animated element missing aria-label');
    }
  }

  public async runAllTests(): Promise<AnimationTestResult[]> {
    const results: AnimationTestResult[] = [];
    
    for (const [testName] of this.tests) {
      try {
        const result = await this.runTest(testName);
        results.push(result);
      } catch (error) {
        console.error(`Failed to run test ${testName}:`, error);
      }
    }

    return results;
  }

  public generateReport(): string {
    const browserInfo = browserCompatibility.getBrowserInfo();
    const capabilities = browserCompatibility.getCapabilities();
    
    let report = `# Cross-Browser Animation Test Report\n\n`;
    report += `**Browser:** ${browserInfo.name} ${browserInfo.version} (${browserInfo.engine})\n`;
    report += `**Performance Profile:** ${browserCompatibility.getPerformanceProfile()}\n`;
    report += `**Reduced Motion:** ${browserInfo.prefersReducedMotion ? 'Yes' : 'No'}\n\n`;

    report += `## Browser Capabilities\n`;
    report += `- Max Concurrent Animations: ${capabilities.maxConcurrentAnimations}\n`;
    report += `- Complex Transforms: ${capabilities.supportsComplexTransforms ? 'Yes' : 'No'}\n`;
    report += `- Backdrop Filter: ${capabilities.supportsBackdropFilter ? 'Yes' : 'No'}\n`;
    report += `- GPU Acceleration: ${capabilities.shouldUseGPUAcceleration ? 'Yes' : 'No'}\n\n`;

    report += `## Test Results\n\n`;
    
    const passedTests = this.results.filter(r => r.passed).length;
    const totalTests = this.results.length;
    
    report += `**Overall:** ${passedTests}/${totalTests} tests passed\n\n`;

    this.results.forEach(result => {
      report += `### ${result.testName}\n`;
      report += `- **Status:** ${result.passed ? '✅ PASSED' : '❌ FAILED'}\n`;
      report += `- **Duration:** ${result.duration.toFixed(2)}ms\n`;
      report += `- **Average FPS:** ${result.performance.averageFPS.toFixed(1)}\n`;
      report += `- **Dropped Frames:** ${result.performance.droppedFrames}\n`;

      if (result.errors.length > 0) {
        report += `- **Errors:**\n`;
        result.errors.forEach(error => {
          report += `  - ${error}\n`;
        });
      }

      if (result.warnings.length > 0) {
        report += `- **Warnings:**\n`;
        result.warnings.forEach(warning => {
          report += `  - ${warning}\n`;
        });
      }

      report += `\n`;
    });

    return report;
  }

  public clearTests(): void {
    this.tests.clear();
    this.results = [];
  }
}

// Export singleton instance
export const crossBrowserTester = new CrossBrowserAnimationTester();

// Utility functions for common animation tests
export const testFramerMotionComponent = async (
  element: HTMLElement,
  testName: string = 'framer-motion-test'
): Promise<AnimationTestResult> => {
  crossBrowserTester.registerTest(testName, element, {
    keyframes: [
      { opacity: 0, transform: 'translateY(20px)' },
      { opacity: 1, transform: 'translateY(0px)' }
    ],
    options: {
      duration: 300,
      easing: 'ease-out',
      fill: 'forwards'
    }
  });

  return crossBrowserTester.runTest(testName);
};

export const testScrollAnimation = async (
  element: HTMLElement,
  testName: string = 'scroll-animation-test'
): Promise<AnimationTestResult> => {
  crossBrowserTester.registerTest(testName, element, {
    keyframes: [
      { transform: 'scale(0.8)', opacity: 0 },
      { transform: 'scale(1)', opacity: 1 }
    ],
    options: {
      duration: 600,
      easing: [0.25, 0.46, 0.45, 0.94],
      fill: 'forwards'
    }
  });

  return crossBrowserTester.runTest(testName);
};

export const test3DTransform = async (
  element: HTMLElement,
  testName: string = '3d-transform-test'
): Promise<AnimationTestResult> => {
  crossBrowserTester.registerTest(testName, element, {
    keyframes: [
      { transform: 'rotateY(0deg) translateZ(0px)' },
      { transform: 'rotateY(180deg) translateZ(50px)' },
      { transform: 'rotateY(360deg) translateZ(0px)' }
    ],
    options: {
      duration: 1000,
      easing: 'ease-in-out',
      fill: 'forwards'
    }
  });

  return crossBrowserTester.runTest(testName);
};